import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { Copy, Plus, Trash2 } from 'lucide-react';
import { display } from '@/lib/utils';
import { Dialog } from '@/components/ui/layout';
import { Button, Checkbox, Input, Switch, TimePicker } from '@/components/ui/primitives';
import {
  addPeriod,
  applyToAllDays,
  daysOverwrittenBy,
  orderedWeekdays,
  patchPeriod,
  periodIssues,
  periodTimeBlocked,
  removePeriod,
  setWorking,
  weekdayLabel,
  type Period,
  type PeriodIssue,
  type WeekHours,
} from './clinic-hours-model';

const PERIOD_GRID = 'grid grid-cols-[6.5rem_6.5rem_5rem_9.5rem_2.25rem] items-center gap-2';
const MAX_OVERBOOK = 20;

const clampOverbook = (raw: string): number => Math.min(MAX_OVERBOOK, Math.max(0, Math.trunc(Number(raw)) || 0));

export function ClinicHoursTable({ week, onChange, weekStartsOn = 1, dayClosesAt = 6 }: { week: WeekHours; onChange: (next: WeekHours) => void; weekStartsOn?: number; dayClosesAt?: number }) {
  const [confirming, setConfirming] = useState<number | null>(null);

  const requestApply = (weekday: number) => {
    if (daysOverwrittenBy(week, weekday).length > 0) setConfirming(weekday);
    else onChange(applyToAllDays(week, weekday));
  };

  return (
    <>
      <div className="overflow-x-auto rounded-md border">
        <table className="w-full min-w-[46rem] text-sm">
          <thead className="bg-muted/50 text-xs text-muted-foreground">
            <tr>
              <th scope="col" className="w-32 px-3 py-2 text-start font-medium">
                {tr('opd', 'day')}
              </th>
              <th scope="col" className="w-32 px-3 py-2 text-start font-medium">
                {tr('opd', 'working')}
              </th>
              <th scope="col" className="px-3 py-2 text-start font-medium">
                <div className={PERIOD_GRID}>
                  <span>{tr('opd', 'from')}</span>
                  <span>{tr('opd', 'to')}</span>
                  <span>{tr('opd', 'overbook')}</span>
                  <span>{tr('opd', 'bookable_online')}</span>
                  <span />
                </div>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {orderedWeekdays(weekStartsOn).map((weekday) => {
              const day = week[weekday];
              const name = weekdayLabel(weekday, display.locale, 'long');
              const issues = periodIssues(day, dayClosesAt);
              return (
                <tr key={weekday} className="align-top">
                  <th scope="row" className="px-3 py-3 text-start font-medium">
                    {name}
                  </th>
                  <td className="px-3 py-3">
                    <Switch
                      checked={day.working}
                      onCheckedChange={(working) => onChange(setWorking(week, weekday, working))}
                      aria-label={`${name}: ${tr('opd', 'working')}`}
                    />
                  </td>
                  <td className="px-3 py-2">
                    {day.working ? (
                      <div className="space-y-2">
                        {day.periods.map((period, index) => (
                          <PeriodRow
                            key={period.key}
                            period={period}
                            index={index}
                            issue={issues.get(period.key)}
                            isBlocked={(side, time) => periodTimeBlocked(day, period.key, side, time, dayClosesAt)}
                            onPatch={(changes) => onChange(patchPeriod(week, weekday, period.key, changes))}
                            onRemove={() => onChange(removePeriod(week, weekday, period.key))}
                          />
                        ))}
                        <div className="flex flex-wrap gap-2">
                          <Button type="button" size="sm" variant="outline" onClick={() => onChange(addPeriod(week, weekday))}>
                            <Plus className="me-1 h-3.5 w-3.5" /> {tr('opd', 'add_period')}
                          </Button>
                          <Button type="button" size="sm" variant="ghost" disabled={issues.size > 0} onClick={() => requestApply(weekday)}>
                            <Copy className="me-1 h-3.5 w-3.5" /> {tr('opd', 'apply_to_all_days')}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <span className="flex h-9 items-center text-muted-foreground">{tr('opd', 'not_working')}</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {confirming !== null && (
        <Dialog
          open
          onClose={() => setConfirming(null)}
          title={tr('opd', 'replace_other_days_title')}
          description={tr('opd', 'replace_other_days_body', {
            count: daysOverwrittenBy(week, confirming).length,
            day: weekdayLabel(confirming, display.locale, 'long'),
          })}
          footer={
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setConfirming(null)}>
                {tr('opd', 'cancel')}
              </Button>
              <Button
                type="button"
                onClick={() => {
                  onChange(applyToAllDays(week, confirming));
                  setConfirming(null);
                }}
              >
                {tr('opd', 'replace_hours')}
              </Button>
            </div>
          }
        >
          <ul className="list-disc ps-5 text-sm">
            {daysOverwrittenBy(week, confirming).map((weekday) => (
              <li key={weekday}>{weekdayLabel(weekday, display.locale, 'long')}</li>
            ))}
          </ul>
        </Dialog>
      )}
    </>
  );
}

function PeriodRow({
  period,
  index,
  issue,
  isBlocked,
  onPatch,
  onRemove,
}: {
  period: Period;
  index: number;
  issue?: PeriodIssue;
  isBlocked: (side: 'from' | 'to', time: string) => boolean;
  onPatch: (changes: Partial<Omit<Period, 'key'>>) => void;
  onRemove: () => void;
}) {
  const invalid = Boolean(issue);
  return (
    <div>
    <div className={PERIOD_GRID}>
      <TimePicker
        value={period.from}
        isTimeDisabled={(time) => isBlocked('from', time)}
        aria-invalid={invalid}
        onChange={(from) => onPatch({ from })}
        step={10}
        hourCycle={24}
        required
        aria-label={tr('opd', 'session_x_starts', { value: index + 1 })}
      />
      <TimePicker
        value={period.to}
        isTimeDisabled={(time) => isBlocked('to', time)}
        aria-invalid={invalid}
        onChange={(to) => onPatch({ to })}
        step={10}
        hourCycle={24}
        required
        aria-label={tr('opd', 'session_x_ends', { value: index + 1 })}
      />
      <Input
        type="number"
        min={0}
        max={MAX_OVERBOOK}
        value={period.overbook}
        onChange={(event) => onPatch({ overbook: clampOverbook(event.target.value) })}
        aria-label={tr('opd', 'overbook')}
      />
      <Checkbox
        checked={period.bookableOnline}
        onCheckedChange={(bookableOnline) => onPatch({ bookableOnline })}
        aria-label={tr('opd', 'bookable_online')}
      />
      <Button type="button" variant="ghost" size="icon" aria-label={tr('opd', 'remove_period')} title={tr('opd', 'remove_period')} onClick={onRemove}>
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
    {issue && (
      <p role="alert" className="mt-1 text-xs text-destructive">
        {issue.kind === 'overlap' ? tr('opd', 'overlaps_with_x', { range: issue.range }) : tr('opd', 'a_session_must_end_after_it_starts_an_evening')}
      </p>
    )}
    </div>
  );
}
